/* Neither sticky nor opaque: both belong to the row-group wrapper this sits inside
   (`gantt-chart.module.scss`'s `.headerGroup`), which is `.canvas`'s direct child and therefore
   the only box here whose sticky containing block is tall enough to travel. That wrapper also
   carries `background: inherit`, which is what puts the day gradient behind these cells — see
   the comment there. This element stays transparent so the wrapper's paint shows through. */
/* TWO FULL-WIDTH STRIPS, not a sidebar corner beside a timeline header. Both the month strip and
   the day ruler under it span the WHOLE canvas, row-header column included, so the header reads as
   one continuous band and the dates stay on screen where the sidebar covers the timeline. Neither
   strip has a spacer holding it clear of the sidebar any more; each positions its
   timeline-coordinate layer with a `left: var(--gantt-sidebar-width)` inset instead. Heights still
   add up to `--gantt-header-height` (2.8rem + 3.6rem). */
.header {
  display: flex;
  flex-direction: column;
  min-height: var(--gantt-header-height);
}

/* The full-width month strip. It carries the band colour itself, so the band a month draws and
   the empty space above the sidebar read as one continuous strip rather than as two boxes that
   happen to touch. */
.bandsRow {
  position: relative;
  height: 2.8rem;
  background-color: var(--color-surfaces-bg-elevation-3);
}

/* Timeline coordinates start `--gantt-sidebar-width` in from the canvas's left edge, and every
   `--band-x` below is measured from `range.startDay` — the same origin as a tick's or a bar's.
   Insetting the LAYER is what keeps that true now that its strip is wider than the timeline;
   the alternative, adding the sidebar width to every band in JS, would put two different origins
   in play for boxes that have to line up to the pixel. */
.bands {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--gantt-sidebar-width);
  width: var(--gantt-range-width);
}

/* The full-width day-ruler strip, laid out exactly like `.bandsRow` above. It has no sticky
   `.corner` spacer: that box used to occupy the first `--gantt-sidebar-width` of the strip,
   opaque, and hid every column that had scrolled behind the row-header column. Dropping it is what
   lets the ruler read continuously across the whole scrollport, sidebar included.

   The strip stays transparent so `.headerGroup`'s inherited day gradient paints through it. That
   gradient is positioned at the timeline origin and REPEATS, so the striping over the sidebar
   arrives in the correct phase for the ticks drawn on it — nothing has to be re-offset here. */
.ticksRow {
  position: relative;
  flex: 1;
}

/* Timeline coordinates again — see `.bands`. Absolute rather than a flex item now that there is no
   spacer ahead of it to push it into place. */
.track {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--gantt-sidebar-width);
  width: var(--gantt-range-width);
}

/* MonthLabel + MonthContainer, from chart-header.tsx:157-198 */
.band {
  position: absolute;
  top: 0;
  left: var(--band-x);
  width: var(--band-w);
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1.2rem;
  box-sizing: border-box;
  background-color: var(--color-surfaces-bg-elevation-3);
  border-inline-end: 0.1rem solid var(--gantt-grid-line);
  font-size: 1.2rem;
  line-height: 1.6rem;
  color: var(--color-texts-high-contrast);
  white-space: nowrap;

  /* The old band was square except at the two outer ends of the range, and the last one dropped
     its divider because there is nothing after it to divide from
     (`gantt-chart/components/chart-header.tsx:182-198`). Bands are DOM siblings spanning the whole
     range — not the visible window — so these match the first and last month, as they did before. */
  &:first-child {
    border-start-start-radius: 0.4rem;
  }

  &:last-child {
    border-start-end-radius: 0.4rem;
    border-inline-end: none;
  }
}

/* StickyLabel, from chart-header.tsx:177-180 — keeps the month name visible while its band
   scrolls past. It pins to the SCROLLPORT's left edge, so the current month is readable on top of
   the row-header column rather than only over the timeline.

   A `left` INSET IS MEASURED FROM THE SCROLLPORT, NOT FROM THE FIRST VISIBLE TIMELINE PIXEL — and
   that is now the point rather than the hazard. The rule previously read
   `calc(var(--gantt-sidebar-width) + 0.8rem)`, pushing the label clear of the sidebar, because a
   sticky `.corner` spacer then ran the full header height: opaque, `z-index: 1` against this
   label's `auto`, so a label pinned at x=12.8 was painted over and the leftmost partially-visible
   month — the exact one this rule exists to keep on screen — was invisible for essentially its
   whole time as the leftmost month.

   That spacer is gone from both strips, so the first pixel of the scrollport is free. THE
   INVARIANT, in the two-clause form: a sticky rule is correct only when (1) its flow position is
   at the leading edge for the inset it uses — always true here — AND (2) the inset clears any
   sticky SIBLING already occupying that edge of the scrollport, OR no such sibling exists in that
   strip. Clause 2 is now satisfied by the restructure rather than by the inset, so moving the
   inset back to the gutter is safe; put any opaque sticky box back over the month strip and it
   breaks again. (`.indicator[data-side='start']` still satisfies clause 2 the other way, with
   `left: var(--gantt-sidebar-width)`, because it does overlap the sticky column.)

   What does NOT change with it: a band whose visible portion already starts right of the gutter
   keeps its natural flow position (a `left` inset only ever pushes a box RIGHT of where flow put
   it), and a band scrolling fully out still slides its label away, because sticky clamps to the
   containing block — `.band`, which is `position: absolute` — so the label can never be pushed
   past its own month's right edge. */
.bandLabel {
  position: sticky;
  left: 0.8rem;
  /* The old label was dimmer than its container: `MonthLabel` set high-contrast and then
     overrode `& > span:last-child` to middle-contrast, and `StickyLabel` was its only child — so
     the container colour never painted any text. Inheriting high-contrast here made every month
     label brighter than the chart this replaced painted it. */
  color: var(--color-texts-middle-contrast);
}

.ticks {
  position: relative;
  height: 3.6rem;
}

/* BaseGanttCell, from styled-gantt.components.ts:10-27 */
.tick {
  position: absolute;
  top: 0;
  left: var(--tick-x);
  width: var(--tick-w);
  height: 3.6rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 1.2rem;
  color: var(--color-texts-low-contrast);
  background-color: transparent;

  &[data-today='true'] {
    color: var(--color-grayscale-white, #fafafa);
    background: var(--gantt-today-line);
  }
}
